import { useId } from '@reach/auto-id'
import i18next from 'i18next'
import { useTranslation } from 'react-i18next'
import ReactTimePicker from 'react-time-picker/dist/entry.nostyle'
import styled from 'styled-components'

import * as themeUtilities from '@/utilities/client-components/utilities/themeUtils'

import Icon from './Icon'

const PickerWrap = styled.div`
  display: flex;
  flex-direction: row;
`

const StyledReactTimePicker = styled(ReactTimePicker)`
  ${({ theme }: any) => themeUtilities.getFontDefinition(theme)};
  background-color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'container')};
  width: 100%;
  appearance: none;
  padding: 5px 4% 5px 5px;
  color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer)};
  border-style: solid;
  border-width: 1px;
  border-radius: 4px;
  border-color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'light')};
  background-image: none;
  background-repeat: no-repeat;
  background-attachment: scroll;
  background-size: 16px 18px;
  background-position: 98% 50%;
  cursor: auto;
  box-shadow: none;
  outline: none;

  /* Custom internal styling for time-picker */

  &.react-time-picker--disabled {
    background-color: ${({ theme, rootContainer }: any) =>
      themeUtilities.getColor(theme, rootContainer, 'secondary', 'root')};
  }

  & .react-time-picker {
    display: inline-flex;
    position: relative;

    &__wrapper {
      border: none;
      display: flex;
      flex-grow: 1;
      flex-shrink: 0;
    }

    &__inputGroup__input {
      color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer)};
    }

    &__inputGroup {
      min-width: calc((4px * 3) + 0.54em * 6 + 0.217em * 2);
      flex-grow: 1;
      padding: 0 2px;
      box-sizing: content-box;
    }

    &__inputGroup__divider {
      padding: 1px 0;
      white-space: pre;
    }

    &__inputGroup__input {
      min-width: 0.54em;
      height: 100%;
      position: relative;
      padding: 0 1px;
      border: 0;
      background: none;
      box-sizing: content-box;
      -moz-appearance: textfield;
    }

    &__inputGroup__input::-webkit-outer-spin-button,
    &__inputGroup__input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &__inputGroup__input:invalid {
      background: rgba(255, 0, 0, 0.1);
    }

    &__inputGroup__input--hasLeadingZero {
      margin-left: -0.54em;
      padding-left: calc(1px + 0.54em);
    }

    &__inputGroup__amPm {
      font: inherit;
      -moz-appearance: menulist;
    }

    &__button {
      border: 0;
      background: transparent;
      padding: 4px 6px;
    }
  }
`
const StyledIcon = styled(Icon)`
  stroke: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'default')};
  fill: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'default')};
`

const Label = styled.label`
  ${({ theme }: any) => themeUtilities.getFontDefinition(theme, { weight: 'bolder' })};
  color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer)};
`

const InputWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

const RequiredIndicator = styled.span`
  color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'attention')};
  padding: 0 5px;
`

const Error = styled.p`
  margin: 5px 0 0;
  background-color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'background')};
  ${({ theme }: any) => themeUtilities.getFontDefinition(theme)};
  color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'attention')};
`

interface TimePickerProps {
  className?: string
  disabled?: boolean
  error?: string
  id?: string
  innerRef?: object
  isClearable?: boolean
  isRequired?: boolean
  label?: string
  maxTime?: unknown | unknown
  minTime?: unknown | unknown
  name?: string
  onChange?(...args: unknown[]): unknown
  rootContainer?: string
  value?: any
}

// TODO Wrap with the standard `Input` component to unify the look/feel of all the inputs
const TimePicker = ({
  rootContainer,
  onChange,
  value,
  disabled,
  isClearable,
  label,
  innerRef,
  isRequired,
  error,
  name,
  className,
  minTime,
  maxTime
}: TimePickerProps) => {
  const { t } = useTranslation('shared')
  const id = useId()

  return (
    // @ts-expect-error
    <PickerWrap className={className} rootContainer={rootContainer} displayAsType={'default'}>
      {isRequired && (
        // @ts-expect-error
        <RequiredIndicator rootContainer={rootContainer} aria-label={t('shared:RequiredIndicator.label')}>
          {t('shared:RequiredIndicator.icon')}
        </RequiredIndicator>
      )}
      <InputWrapper>
        {/* @ts-expect-error */}
        {label && <Label htmlFor={id}>{label}</Label>}
        {/* @ts-expect-error */}
        <StyledReactTimePicker
          id={id}
          disableClock={true}
          rootContainer={rootContainer}
          displayAsType={disabled ? 'disabled' : 'default'}
          onChange={onChange}
          value={value}
          disabled={disabled}
          isClearable={isClearable}
          locale={i18next.languages[0]}
          ref={innerRef}
          name={name}
          amPmAriaLabel={t('TimePicker.amPmAriaLabel')}
          clearAriaLabel={isClearable ? t('TimePicker.clearAriaLabel') : false}
          hourAriaLabel={t('TimePicker.hourAriaLabel')}
          minuteAriaLabel={t('TimePicker.minuteAriaLabel')}
          nativeInputAriaLabel={t('TimePicker.nativeInputAriaLabel')}
          secondAriaLabel={t('TimePicker.secondAriaLabel')}
          hourPlaceholder={t('TimePicker.hourPlaceholder')}
          minutePlaceholder={t('TimePicker.minutePlaceholder')}
          secondPlaceholder={t('TimePicker.secondPlaceholder')}
          // @ts-expect-error
          clearIcon={isClearable ? <StyledIcon type="fill" name={'clear'} /> : false}
          minTime={minTime}
          maxTime={maxTime}
        />
        {/* @ts-expect-error */}
        {error && <Error rootContainer={rootContainer}>{error}</Error>}
      </InputWrapper>
    </PickerWrap>
  )
}

TimePicker.displayName = 'TimePicker'

TimePicker.defaultProps = {
  rootContainer: 'default',
  isClearable: true,
  onChange: () => {},
  onCalendarClose: () => {}
}

export default TimePicker
